/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * videoStore.ts — 개념 영상 & 크리에이터 크레딧 전역 상태
 *
 * 【흐름】
 *   1. 학생이 개념 영상 시청 → recordView() 호출
 *   2. 유효 시청(30초↑) 확정 → validView 카운트 +1
 *   3. 크리에이터 크레딧 += 10원 (CREDIT_PER_VIEW)
 *   4. 관리자가 pending 영상 검수 → approve / reject
 *
 * 【Phase 1】localStorage + YouTube 임베드
 * 【Phase 2】백엔드 API + S3 자체 업로드로 교체 예정
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

import { create } from 'zustand'

// ──────────────────────────────────────────────────────────────
// 상수
// ──────────────────────────────────────────────────────────────
export const CREDIT_PER_VIEW = 10          // 유효 시청 1회당 크레딧 (원)
export const MIN_WATCH_SEC   = 30          // 유효 시청 최소 초
export const MIN_WATCH_RATIO = 0.5         // 유효 시청 최소 비율 (50%)
const STORAGE_KEY = 'math_video_store_v1'

// ──────────────────────────────────────────────────────────────
// 타입 정의
// ──────────────────────────────────────────────────────────────

/** 영상 상태
 *  pending    = 크리에이터 제출 후 관리자 검수 대기
 *  approved   = 검수 완료, 학생에게 공개
 *  rejected   = 반려
 *  recruiting = AI가 자동 생성한 "영상 모집 중" 슬롯
 *               (실제 영상 없음 — 크리에이터 지원 유도용)
 */
export type VideoStatus = 'pending' | 'approved' | 'rejected' | 'recruiting'

/** 업로드 방식 */
export type VideoSource = 'youtube' | 'upload'

/**
 * 개념 영상 레코드
 * concept_id 는 LearnPage 의 step ID (예: 'LS05') 또는
 * ProblemPage/TestPage 의 concept 태그 (예: '기울기')
 */
export interface ConceptVideo {
  id:           string        // UUID (Date.now() 기반)
  conceptId:    string        // 'LS05' | '기울기' | 'x절편·y절편' …
  conceptLabel: string        // 화면 표시용 '기울기(slope) 이해'
  title:        string        // 영상 제목
  creatorId:    string        // 크리에이터 ID (이메일 or UUID)
  creatorName:  string        // 화면 표시용 이름
  source:       VideoSource
  youtubeUrl?:  string        // source === 'youtube' 일 때
  uploadPath?:  string        // source === 'upload' 일 때 (Phase 2)
  thumbnailUrl: string        // 썸네일
  durationSec:  number        // 영상 길이 (초)
  description:  string        // 간단 소개 (200자 이내)
  tags:         string[]      // ['기울기', '변화율', '중2']
  status:       VideoStatus
  viewCount:    number        // 총 시청수
  validViewCount: number      // 유효 시청수 (크레딧 계산 기준)
  rating:       number        // 평균 평점 (0~5)
  ratingCount:  number
  adminNote?:   string        // 검수 메모
  submittedAt:  string        // ISO
  approvedAt?:  string
}

/** 시청 기록 (어뷰징 방지) */
export interface ViewRecord {
  id:         string
  videoId:    string
  viewerId:   string          // 학생 ID
  watchedSec: number
  isValid:    boolean
  conceptId:  string
  viewedAt:   string
}

/** 크레딧 원장 */
export interface CreditEntry {
  id:         string
  creatorId:  string
  videoId:    string
  viewId:     string
  amount:     number          // CREDIT_PER_VIEW
  status:     'pending' | 'paid'
  createdAt:  string
}

/** 크리에이터 프로필 */
export interface CreatorProfile {
  id:          string
  name:        string
  email:       string
  bio:         string
  bankInfo?:   string        // 정산용 (실제로는 암호화 필요)
  totalCredit: number        // 누적 크레딧 (원)
  paidCredit:  number        // 정산 완료 크레딧
  joinedAt:    string
}

/** 학생 평점 */
export interface VideoRating {
  videoId:   string
  viewerId:  string
  score:     number          // 1~5
  ratedAt:   string
}

// ──────────────────────────────────────────────────────────────
// 스토리지 어댑터
// ──────────────────────────────────────────────────────────────
interface VideoStoreData {
  videos:    ConceptVideo[]
  views:     ViewRecord[]
  credits:   CreditEntry[]
  creators:  CreatorProfile[]
  ratings:   VideoRating[]
  updatedAt: string
}

function makeDefault(): VideoStoreData {
  return {
    videos:   SEED_VIDEOS,     // 초기 시드 데이터
    views:    [],
    credits:  [],
    creators: SEED_CREATORS,
    ratings:  [],
    updatedAt: new Date().toISOString(),
  }
}

const storage = {
  load(): VideoStoreData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return makeDefault()
      const parsed = JSON.parse(raw) as VideoStoreData
      // 시드 영상이 없으면 추가 (앱 업데이트 시 새 영상 반영)
      const existingIds = new Set(parsed.videos.map(v => v.id))
      const newSeeds = SEED_VIDEOS.filter(v => !existingIds.has(v.id))
      if (newSeeds.length > 0) {
        parsed.videos = [...newSeeds, ...parsed.videos]
      }
      return parsed
    } catch {
      return makeDefault()
    }
  },
  save(data: VideoStoreData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, updatedAt: new Date().toISOString() }))
    } catch {
      console.warn('[videoStore] 저장 실패')
    }
  },
}

// ──────────────────────────────────────────────────────────────
// 시드 데이터 — 테스트용 영상
// ──────────────────────────────────────────────────────────────
const SEED_CREATORS: CreatorProfile[] = [
  {
    id: 'creator_seed_01',
    name: '수학 채널 테스트',
    email: 'test@math.exko.kr',
    bio: '테스트용 시드 크리에이터',
    totalCredit: 0,
    paidCredit: 0,
    joinedAt: '2025-01-01T00:00:00.000Z',
  },
]

/**
 * YouTube 영상 ID 추출 유틸
 * https://youtu.be/Zu933iLZnME?si=... → Zu933iLZnME
 */
export function extractYouTubeId(url: string): string | null {
  const patterns = [
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
  ]
  for (const p of patterns) {
    const m = url.match(p)
    if (m) return m[1]
  }
  return null
}

export function getYouTubeEmbedUrl(url: string): string | null {
  const id = extractYouTubeId(url)
  return id ? `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1` : null
}

export function getYouTubeThumbnail(url: string): string {
  const id = extractYouTubeId(url)
  return id
    ? `https://img.youtube.com/vi/${id}/mqdefault.jpg`
    : 'https://placehold.co/320x180/e2e8f0/94a3b8?text=No+Thumbnail'
}

// ── 시드 영상 생성 헬퍼 ────────────────────────────────────────────
/** 영상 모집 중 슬롯 — 실제 URL 없이 크리에이터 지원 유도 */
function makeRecruitSlot(
  id: string,
  conceptId: string,
  conceptLabel: string,
  title: string,
  description: string,
  tags: string[]
): ConceptVideo {
  return {
    id,
    conceptId,
    conceptLabel,
    title,
    creatorId:    'system',
    creatorName:  'AI 튜터 시스템',
    source:       'youtube',
    youtubeUrl:   '',
    thumbnailUrl: `https://placehold.co/320x180/f1f5f9/64748b?text=${encodeURIComponent(conceptLabel)}`,
    durationSec:  0,
    description,
    tags,
    status:       'recruiting',
    viewCount:    0,
    validViewCount: 0,
    rating:       0,
    ratingCount:  0,
    submittedAt:  '2025-05-01T00:00:00.000Z',
  }
}

const SEED_VIDEOS: ConceptVideo[] = [
  // ── LS05: 기울기 — 실제 YouTube 영상 (승인 완료) ─────────────────
  {
    id: 'video_seed_slope_01',
    conceptId:    'LS05',
    conceptLabel: '기울기(slope) 이해',
    title:        '기울기란 무엇인가? — 핵심 개념 5분 완성',
    creatorId:    'creator_seed_01',
    creatorName:  '수학 채널 테스트',
    source:       'youtube',
    youtubeUrl:   'https://youtu.be/Zu933iLZnME?si=l_8WoEW_Cd1E6Kbs',
    thumbnailUrl: getYouTubeThumbnail('https://youtu.be/Zu933iLZnME'),
    durationSec:  300,
    description:  '일차함수 y=ax+b에서 기울기 a의 의미를 시각적으로 쉽게 설명합니다. 변화율과 그래프 방향 관계를 5분 안에 완벽히 이해할 수 있습니다.',
    tags:         ['기울기', '변화율', '1차함수', '중2'],
    status:       'approved',
    viewCount:    0,
    validViewCount: 0,
    rating:       0,
    ratingCount:  0,
    submittedAt:  '2025-04-30T00:00:00.000Z',
    approvedAt:   '2025-04-30T00:00:00.000Z',
  },

  // ── LS01: 변수 이해 (M01 관련 파편) ─────────────────────────────
  makeRecruitSlot(
    'slot_LS01_a', 'LS01', '변수 이해',
    '[영상 모집 중] 변수 vs 상수 — 실생활로 3분 만에 구별하기',
    '변수(x, y)와 상수의 차이를 "오늘 기온"과 "물의 끓는 점"으로 쉽게 설명하는 영상을 모집합니다. 5분 이내, 중2 수준.',
    ['변수', '상수', '식', '중2', '1차함수 기초']
  ),
  makeRecruitSlot(
    'slot_LS01_b', 'LS01', '변수 이해',
    '[영상 모집 중] M01 오개념 교정 — "변수는 모르는 숫자"가 아니다!',
    '변수를 단순히 "모르는 숫자"로만 보는 M01 오개념을 교정합니다. 변수는 여러 값을 가질 수 있는 양임을 시각적으로 설명하세요.',
    ['변수', '오개념 M01', '1차함수', '중2']
  ),

  // ── LS02: 순서쌍과 좌표평면 (M02 관련 파편) ──────────────────────
  makeRecruitSlot(
    'slot_LS02_a', 'LS02', '순서쌍과 좌표평면',
    '[영상 모집 중] 좌표평면 4사분면 — 부호로 위치 읽기 완전 정리',
    '순서쌍 (x, y)에서 x와 y의 부호에 따라 어느 사분면인지 빠르게 판별하는 방법을 시각적으로 설명하는 영상을 모집합니다.',
    ['순서쌍', '좌표평면', '사분면', '중2']
  ),
  makeRecruitSlot(
    'slot_LS02_b', 'LS02', '순서쌍과 좌표평면',
    '[영상 모집 중] M02 오개념 교정 — (x, y)에서 x와 y를 바꾸면 안 되는 이유',
    '(x, y)에서 x가 먼저(가로축), y가 나중(세로축)임을 못 그림으로 직관적으로 보여주는 영상. M02 오개념 집중 공략.',
    ['순서쌍', '좌표', 'x·y 순서', '오개념 M02', '중2']
  ),

  // ── LS03: 표에서 함수 관계 찾기 ───────────────────────────────────
  makeRecruitSlot(
    'slot_LS03_a', 'LS03', '표에서 함수 관계 찾기',
    '[영상 모집 중] 표에서 함수 규칙 발견 — 하나의 x에 하나의 y',
    'x와 y의 대응 표를 보고 "x 하나에 y 하나"라는 함수 정의를 발견하는 방법. 함수 O/X 판별 포함.',
    ['함수', '표', '대응', '함수 정의', '중2']
  ),
  makeRecruitSlot(
    'slot_LS03_b', 'LS03', '표에서 함수 관계 찾기',
    '[영상 모집 중] 함수인가 아닌가? — 표 보고 3초 만에 판별하기',
    '표에서 x값이 중복될 때 함수인지 아닌지 판별하는 시각적 방법을 설명하는 영상을 모집합니다.',
    ['함수 판별', '일대일 대응', '표', '중2']
  ),

  // ── LS04: 변화율 (M06 관련 파편) ─────────────────────────────────
  makeRecruitSlot(
    'slot_LS04_a', 'LS04', '변화율(율)',
    '[영상 모집 중] Δy÷Δx — 변화율 계산 순서를 절대 잊지 않는 방법',
    'x 변화량 대비 y 변화량(Δy/Δx) 계산 순서를 "분자=y, 분모=x" 암기법으로 굳히는 영상. M06 오개념 교정.',
    ['변화율', 'ΔyΔx', '오개념 M06', '중2']
  ),
  makeRecruitSlot(
    'slot_LS04_b', 'LS04', '변화율(율)',
    '[영상 모집 중] 변화율 실생활 — 속도·요금표로 Δy/Δx 체감하기',
    '택시 요금표·자동차 속도 등 실생활 표를 이용해 변화율(기울기의 전 단계)을 직관적으로 이해하는 영상을 모집합니다.',
    ['변화율', '기울기', '실생활', '중2']
  ),

  // ── LS06: y절편과 x절편 (M03 관련 파편) ──────────────────────────
  makeRecruitSlot(
    'slot_LS06_a', 'LS06', 'y절편과 x절편',
    '[영상 모집 중] y절편·x절편 위치 — 축과 만나는 점을 헷갈리지 않는 법',
    'y절편(y축과 만나는 점, x=0 대입)과 x절편(x축과 만나는 점, y=0 대입)을 그래프 위에서 직접 짚어주는 영상.',
    ['y절편', 'x절편', '절편', '그래프', '중2']
  ),
  makeRecruitSlot(
    'slot_LS06_b', 'LS06', 'y절편과 x절편',
    '[영상 모집 중] M03 오개념 교정 — y=2x+3에서 기울기가 3? y절편이 2?',
    'y=ax+b에서 a(기울기)와 b(y절편) 위치를 혼동하는 M03 오개념을 시각적으로 완전 교정하는 영상.',
    ['y절편', '기울기 혼동', '오개념 M03', 'y=ax+b', '중2']
  ),

  // ── LS07: 1차 함수식 y = ax + b ───────────────────────────────────
  makeRecruitSlot(
    'slot_LS07_a', 'LS07', '1차 함수식 y = ax + b',
    '[영상 모집 중] y = ax + b 식 세우기 3단계 — 두 점으로 완성',
    '기울기 a 계산 → y절편 b 계산 → 식 완성하는 3단계 흐름을 두 점 예제로 보여주는 영상을 모집합니다.',
    ['일차함수', 'y=ax+b', '식 세우기', '중2']
  ),
  makeRecruitSlot(
    'slot_LS07_b', 'LS07', '1차 함수식 y = ax + b',
    '[영상 모집 중] 함수값 계산 — x 대입해서 y 구하기 완전 정복',
    'y=ax+b에 특정 x값을 대입해 y값(함수값)을 구하는 계산 연습 영상. 실수 유형 포함.',
    ['함수값', '대입', 'y=ax+b', '계산', '중2']
  ),

  // ── LS08: 정비례 vs 1차 함수 (M04 관련 파편) ─────────────────────
  makeRecruitSlot(
    'slot_LS08_a', 'LS08', '정비례 vs 1차 함수',
    '[영상 모집 중] M04 오개념 교정 — "y=2x는 1차 함수가 아니다"가 틀린 이유',
    '정비례 y=ax도 1차 함수(y절편=0인 특수한 경우)임을 설명하는 영상. M04 오개념 집중 교정.',
    ['정비례', '일차함수', '원점', 'y절편=0', '오개념 M04', '중2']
  ),
  makeRecruitSlot(
    'slot_LS08_b', 'LS08', '정비례 vs 1차 함수',
    '[영상 모집 중] 정비례와 일차함수 그래프 비교 — 원점 통과 여부가 핵심!',
    'y=2x(원점 통과)와 y=2x+3(원점 미통과)의 그래프 차이를 나란히 그려서 비교하는 영상을 모집합니다.',
    ['정비례', '일차함수', '그래프 비교', '원점', '중2']
  ),

  // ── LS09: 그래프 그리기 (M07 관련 파편) ──────────────────────────
  makeRecruitSlot(
    'slot_LS09_a', 'LS09', '그래프 그리기',
    '[영상 모집 중] 두 점으로 직선 그리기 — y절편·x절편 찍고 연결!',
    'y=ax+b에서 x=0(y절편)과 y=0(x절편) 두 점을 구해 직선을 그리는 과정을 손으로 직접 그리며 보여주는 영상.',
    ['그래프', 'x절편', 'y절편', '두 점', '직선', '중2']
  ),
  makeRecruitSlot(
    'slot_LS09_b', 'LS09', '그래프 그리기',
    '[영상 모집 중] M07 오개념 교정 — x절편과 y절편을 바꿔 읽는 실수 0으로 만들기',
    'x절편은 x축과 만나는 점(y=0), y절편은 y축과 만나는 점(x=0)임을 그래프 위에서 직접 짚어 혼동을 제거하는 영상.',
    ['x절편', 'y절편', '혼동', '오개념 M07', '그래프', '중2']
  ),

  // ── LS10: 그래프에서 식 구하기 (M08 관련 파편) ───────────────────
  makeRecruitSlot(
    'slot_LS10_a', 'LS10', '그래프에서 식 구하기',
    '[영상 모집 중] 그래프 → 함수식 역추적 3단계 — 두 점 읽기부터 식 완성까지',
    '그래프에서 두 점의 좌표를 읽고 기울기→y절편→식 완성하는 3단계를 영상으로 설명합니다.',
    ['그래프 읽기', '역추적', '기울기', '함수식', '중2']
  ),
  makeRecruitSlot(
    'slot_LS10_b', 'LS10', '그래프에서 식 구하기',
    '[영상 모집 중] M08 오개념 교정 — 기울기를 Δx/Δy로 거꾸로 쓰는 실수 없애기',
    '기울기 = Δy/Δx(세로변화/가로변화)임을 그래프 위 삼각형으로 시각적으로 확인하는 영상. M08 오개념 완전 교정.',
    ['기울기', 'Δy/Δx 순서', '오개념 M08', '그래프', '중2']
  ),
  makeRecruitSlot(
    'slot_LS10_c', 'LS10', '그래프에서 식 구하기',
    '[영상 모집 중] 눈금 단위 조심! — 그래프 축 눈금 간격 확인법',
    '그래프에서 축 눈금 간격이 1이 아닐 때 기울기를 잘못 읽는 실수를 방지하는 방법을 설명하는 영상.',
    ['그래프 읽기', '눈금 간격', 'M07', '실수 방지', '중2']
  ),

  // ── LS11: 실생활 문장제 → 함수식 (M08 관련 파편) ─────────────────
  makeRecruitSlot(
    'slot_LS11_a', 'LS11', '실생활 문장제 → 함수식',
    '[영상 모집 중] 문장제 → 함수식 4단계 — 택시 요금 예제로 완벽 정리',
    '문장제에서 "기준값(y절편) → 변화율(기울기) → 식 → 검산" 4단계를 택시 요금 예제로 설명하는 영상을 모집합니다.',
    ['문장제', '택시 요금', '4단계', '모델링', '중2']
  ),
  makeRecruitSlot(
    'slot_LS11_b', 'LS11', '실생활 문장제 → 함수식',
    '[영상 모집 중] M08 교정 — "분마다 3 감소"를 a=3으로 쓰면 안 되는 이유',
    '"1분마다 3 감소"는 a=-3(음수 기울기)임을 이해시키는 영상. 방향과 부호를 항상 함께 파악하는 습관 형성.',
    ['문장제', '음수 기울기', '오개념 M08', '방향', '중2']
  ),

  // ── LS12: 오답 분석과 개념 정리 ───────────────────────────────────
  makeRecruitSlot(
    'slot_LS12_a', 'LS12', '오답 분석과 개념 정리',
    '[영상 모집 중] 1차함수 8대 오개념 총정리 — M01~M08 한 번에 완전 교정',
    '기울기·y절편 혼동(M03), 정비례 오해(M04), 음수 기울기 오해(M05), 변화율 역산(M06) 등 8대 오개념을 한 영상에 정리.',
    ['오개념', 'M01~M08', '종합 정리', '1차함수', '중2']
  ),
  makeRecruitSlot(
    'slot_LS12_b', 'LS12', '오답 분석과 개념 정리',
    '[영상 모집 중] 1차함수 시험 직전 10분 요약 — 수포자도 이건 된다',
    '시험에 꼭 나오는 1차함수 핵심 공식·유형 10개를 10분에 압축 정리하는 영상. 수포자 탈출 프로젝트용.',
    ['시험 준비', '수포자 탈출', '핵심 정리', '1차함수', '중2']
  ),

  // ══════════════════════════════════════════════════════════════
  // 중1 소인수분해 (N1-1) — NS01~NS08 파편 학습 슬롯
  // ══════════════════════════════════════════════════════════════

  // ── NS01: 소수와 합성수 ────────────────────────────────────────
  makeRecruitSlot(
    'slot_NS01_a', 'NS01', '소수와 합성수',
    '[영상 모집 중] 소수 vs 합성수 — 약수 개수로 한 번에 구별하는 법',
    '약수가 딱 2개(1과 자기 자신)이면 소수, 그 이상이면 합성수임을 에라토스테네스의 체로 시각적으로 설명하는 영상.',
    ['소수', '합성수', '약수', '에라토스테네스의 체', '중1']
  ),
  makeRecruitSlot(
    'slot_NS01_b', 'NS01', '소수와 합성수',
    '[영상 모집 중] 수포자 탈출 — 1은 소수가 아닌 이유 완전 정복',
    '1이 소수가 아닌 이유(약수가 1개뿐), 2가 유일한 짝수 소수인 이유를 쉽게 설명하는 영상.',
    ['소수', '1의 정의', '오개념 NM01', '수포자', '중1']
  ),

  // ── NS02: 거듭제곱과 지수 ──────────────────────────────────────
  makeRecruitSlot(
    'slot_NS02_a', 'NS02', '거듭제곱과 지수',
    '[영상 모집 중] 거듭제곱 완전 정복 — 2³은 2×3이 아니라 2×2×2!',
    '밑(base)과 지수(exponent)의 개념을 구별하고, 거듭제곱을 직접 계산하는 과정을 보여주는 영상.',
    ['거듭제곱', '지수', '밑', '오개념 NM03', '중1']
  ),
  makeRecruitSlot(
    'slot_NS02_b', 'NS02', '거듭제곱과 지수',
    '[영상 모집 중] 지수 계산 실전 — 2⁴, 3³, 5² 빠르게 계산하는 법',
    '거듭제곱을 단계별로 계산하는 방법을 손으로 직접 쓰며 보여주는 영상.',
    ['거듭제곱', '지수 계산', '실전', '중1']
  ),

  // ── NS03: 소인수분해 방법 ──────────────────────────────────────
  makeRecruitSlot(
    'slot_NS03_a', 'NS03', '소인수분해',
    '[영상 모집 중] 소인수분해 나눗셈 사다리 — 2부터 나누면 누구나 된다!',
    '나눗셈 사다리(division ladder) 방법으로 모든 자연수를 소인수분해하는 과정을 단계별로 시각화하는 영상.',
    ['소인수분해', '나눗셈 사다리', '소수', '중1', '수포자']
  ),
  makeRecruitSlot(
    'slot_NS03_b', 'NS03', '소인수분해',
    '[영상 모집 중] 인수분해 나무(Factor Tree)로 소인수분해하기',
    '인수분해 나무로 72=2³×3² 처럼 단계적으로 쪼개는 방법을 시각적으로 보여주는 영상.',
    ['소인수분해', '인수분해 나무', 'factor tree', '중1']
  ),
  makeRecruitSlot(
    'slot_NS03_c', 'NS03', '소인수분해',
    '[영상 모집 중] 오개념 NM05 교정 — 36=4×9는 소인수분해가 아니에요!',
    '소인수분해는 반드시 소수만의 곱으로 나타내야 한다는 것을 강조하는 오개념 교정 영상.',
    ['소인수분해', '오개념 NM05', '소수', '합성수', '중1']
  ),

  // ── NS04: 최대공약수 ───────────────────────────────────────────
  makeRecruitSlot(
    'slot_NS04_a', 'NS04', '최대공약수(GCD)',
    '[영상 모집 중] GCD 완전 정복 — 소인수분해로 최대공약수 구하기',
    '두 수를 소인수분해한 뒤 공통 소인수의 낮은 지수를 선택하는 방법을 시각적으로 설명하는 영상.',
    ['최대공약수', 'GCD', '소인수분해', '공통 소인수', '중1']
  ),
  makeRecruitSlot(
    'slot_NS04_b', 'NS04', '최대공약수(GCD)',
    '[영상 모집 중] GCD 오개념 NM07 교정 — 지수가 작은 것을 선택하는 이유',
    'GCD에서 지수가 작은 것을 선택해야 하는 이유를 직관적으로 설명하는 영상. LCM과의 차이점 명확히.',
    ['최대공약수', 'GCD', '오개념 NM07', '지수 선택', '중1']
  ),

  // ── NS05: 최소공배수 ───────────────────────────────────────────
  makeRecruitSlot(
    'slot_NS05_a', 'NS05', '최소공배수(LCM)',
    '[영상 모집 중] LCM 완전 정복 — 소인수분해로 최소공배수 구하기',
    '두 수를 소인수분해한 뒤 모든 소인수의 높은 지수를 선택하는 방법을 시각적으로 설명하는 영상.',
    ['최소공배수', 'LCM', '소인수분해', '모든 소인수', '중1']
  ),
  makeRecruitSlot(
    'slot_NS05_b', 'NS05', '최소공배수(LCM)',
    '[영상 모집 중] GCD × LCM = a × b 공식 증명과 활용',
    'GCD와 LCM의 관계(GCD×LCM=a×b)를 증명하고 실제 문제에 적용하는 방법을 보여주는 영상.',
    ['최소공배수', 'LCM', 'GCD', '관계 공식', '중1']
  ),

  // ── NS06: GCD·LCM 활용 ────────────────────────────────────────
  makeRecruitSlot(
    'slot_NS06_a', 'NS06', 'GCD·LCM 활용',
    '[영상 모집 중] GCD 활용 — 직사각형을 정사각형 타일로 채우기',
    '가로 48cm, 세로 36cm 직사각형을 가장 큰 정사각형 타일로 채우는 문제를 GCD로 해결하는 영상.',
    ['GCD 활용', '타일', '직사각형', '실생활', '중1']
  ),
  makeRecruitSlot(
    'slot_NS06_b', 'NS06', 'GCD·LCM 활용',
    '[영상 모집 중] LCM 활용 — 버스 시간표, 신호등 문제 완전 정복',
    '"A는 3분마다, B는 5분마다" 형식의 LCM 활용 문제를 단계별로 풀어주는 영상.',
    ['LCM 활용', '버스', '신호등', '실생활', '중1']
  ),
  makeRecruitSlot(
    'slot_NS06_c', 'NS06', 'GCD·LCM 활용',
    '[영상 모집 중] NM10 오개념 교정 — 나눠주는 문제는 GCD, 만나는 문제는 LCM',
    '나눠주기→GCD, 동시에 만나기→LCM 판단 기준을 명확히 정리하는 오개념 교정 영상.',
    ['GCD vs LCM', '활용 판단', '오개념 NM10', '실생활', '중1']
  ),

  // ── NS07: 오답 분석과 개념 정리 ──────────────────────────────
  makeRecruitSlot(
    'slot_NS07_a', 'NS07', '소인수분해 오답 분석',
    '[영상 모집 중] 소인수분해 단원 오개념 총정리 — NM01~NM12 한 번에!',
    '소인수분해 단원의 주요 오개념(1=소수 착각, 거듭제곱 계산 오류, GCD/LCM 지수 선택 등)을 총정리하는 영상.',
    ['오개념', 'NM01~NM12', '종합 정리', '소인수분해', '중1']
  ),

  // ── NS08: 수포자 탈출 ─────────────────────────────────────────
  makeRecruitSlot(
    'slot_NS08_a', 'NS08', '수포자를 위한 소인수분해',
    '[영상 모집 중] 수포자 탈출 — 나눗셈만 할 수 있으면 소인수분해 완성!',
    '소인수분해가 무서운 수포자를 위한 친절한 입문 영상. "작은 소수부터 나누면 끝" 3단계만 기억하면 됩니다.',
    ['수포자 탈출', '소인수분해 입문', '처음부터', '친절한 설명', '중1']
  ),
  makeRecruitSlot(
    'slot_NS08_b', 'NS08', '수포자를 위한 소인수분해',
    '[영상 모집 중] 소인수분해 5분 만에 이해하기 — 피자 조각으로 배우는 약수',
    '소수·약수·소인수분해를 피자 나누기 비유로 쉽게 설명하는 수포자 전용 영상.',
    ['수포자 탈출', '소인수분해', '비유', '피자', '약수', '중1']
  ),
]

/**
 * conceptId ↔ 개념 레이블 매핑
 * - ProblemPage / TestPage 의 concept 태그 → LS step ID
 * - getVideosByConceptTag() 가 이 테이블을 먼저 참조
 * - 여기 없으면 태그 부분 일치 검색으로 폴백
 *
 * 커버 범위:
 *   ProblemPage concept 태그 12종
 *   TestPage concept 태그 18종 (중복 제거 후 순 추가분)
 */
export const CONCEPT_TO_STEP: Record<string, string> = {
  // ── LS01: 변수·상수 ────────────────────────────────────────────
  '변수':               'LS01',
  '변수·상수':          'LS01',
  '상수':               'LS01',
  '오개념 M01':         'LS01',

  // ── LS02: 좌표·사분면 ─────────────────────────────────────────
  '좌표평면':           'LS02',
  '좌표평면·사분면':    'LS02',   // TestPage T02
  '그래프·사분면':      'LS02',   // TestPage T14
  '순서쌍':             'LS02',
  '사분면':             'LS02',
  'x·y 순서':           'LS02',
  '오개념 M02':         'LS02',

  // ── LS03: 함수 관계·대응표 ────────────────────────────────────
  '함수 판별':          'LS03',
  '일대일 대응':        'LS03',
  '함수 정의':          'LS03',

  // ── LS04: 변화율 ──────────────────────────────────────────────
  '변화율':             'LS04',
  '변화율·기울기':      'LS04',   // TestPage T06, T16
  'ΔyΔx':              'LS04',
  'Δy/Δx 순서':        'LS04',   // ConceptVideoModal 태그에서 직접 검색 시
  '오개념 M06':         'LS04',

  // ── LS05: 기울기 ──────────────────────────────────────────────
  '기울기':             'LS05',
  '기울기·방향성':      'LS05',
  '기울기·y절편':       'LS05',   // TestPage T01
  '평행·기울기':        'LS05',
  '평행 조건':          'LS05',   // TestPage T13
  '오개념 M05':         'LS05',

  // ── LS06: 절편 ────────────────────────────────────────────────
  'x절편':              'LS06',   // TestPage T03
  'y절편':              'LS06',
  'x절편·y절편':        'LS06',
  'x절편·역계산':       'LS06',   // TestPage T18
  '절편':               'LS06',
  '기울기 혼동':        'LS06',   // M03 오개념 (기울기·y절편 위치 혼동)
  '오개념 M03':         'LS06',

  // ── LS07: 함수식 y = ax + b ──────────────────────────────────
  '일차함수 식':        'LS07',
  '일차함수 정의':      'LS07',   // TestPage T12
  '함수값 계산':        'LS07',   // TestPage T17
  '함수값':             'LS07',
  '대입':               'LS07',
  '식 세우기':          'LS07',
  '연립방정식 활용':    'LS07',
  '연립방정식·교점':    'LS10',   // TestPage T11 (그래프 교점)

  // ── LS08: 정비례 ──────────────────────────────────────────────
  '정비례·원점':        'LS08',   // TestPage T05
  '정비례':             'LS08',
  'y절편=0':            'LS08',
  '그래프 비교':        'LS08',
  '오개념 M04':         'LS08',

  // ── LS09: 그래프 그리기 ───────────────────────────────────────
  '넓이 계산 응용':     'LS09',
  '넓이 계산·응용':     'LS09',   // ProblemPage
  '두 점':              'LS09',
  '직선':               'LS09',
  '혼동':               'LS09',   // x절편·y절편 혼동 태그
  '오개념 M07':         'LS09',

  // ── LS10: 그래프 읽기·역추적 ─────────────────────────────────
  '그래프 읽기':        'LS10',
  '그래프 읽기·기울기': 'LS10',   // TestPage T08
  '역추적':             'LS10',
  '눈금 간격':          'LS10',
  '오개념 M08':         'LS10',

  // ── LS11: 실생활 문장제 ───────────────────────────────────────
  '함수 모델링':        'LS11',
  '함수 모델링·실생활': 'LS11',   // TestPage T15
  '문장제':             'LS11',
  '모델링':             'LS11',
  '음수 기울기':        'LS11',   // M08 방향 오개념
  '방향':               'LS11',

  // ── LS12: 오답 종합 ───────────────────────────────────────────
  '종합':               'LS12',
  '종합·기울기·y절편':  'LS12',   // TestPage T20
  'M01~M08':            'LS12',
  '시험 준비':          'LS12',
  '수포자 탈출':        'LS12',
  '핵심 정리':          'LS12',

  // ══════════════════════════════════════════════════════════════
  // 중1 소인수분해 (N1-1) — NS01~NS08
  // ══════════════════════════════════════════════════════════════
  // NS01: 소수·합성수
  '소수':               'NS01',
  '합성수':             'NS01',
  '소수·합성수':        'NS01',
  '에라토스테네스의 체': 'NS01',

  // NS02: 거듭제곱
  '거듭제곱':           'NS02',
  '지수':               'NS02',
  '거듭제곱과 지수':    'NS02',
  '밑':                 'NS02',

  // NS03: 소인수분해
  '소인수분해':         'NS03',
  '소인수':             'NS03',
  '나눗셈 사다리':      'NS03',
  'factor tree':        'NS03',
  '인수분해 나무':      'NS03',

  // NS04: 최대공약수
  '최대공약수':         'NS04',
  'GCD':                'NS04',
  '공약수':             'NS04',
  '최대공약수(GCD)':    'NS04',

  // NS05: 최소공배수
  '최소공배수':         'NS05',
  'LCM':                'NS05',
  '공배수':             'NS05',
  '최소공배수(LCM)':    'NS05',
  'GCD·LCM 관계':       'NS05',

  // NS06: GCD·LCM 활용
  'GCD 활용':           'NS06',
  'LCM 활용':           'NS06',
  'GCD·LCM 활용':       'NS06',
  '타일':               'NS06',
  '버스':               'NS06',
  '신호등':             'NS06',

  // NS07: 오답 분석
  '소인수분해 오답 분석': 'NS07',
  'NM01~NM12':          'NS07',

  // NS08: 수포자 탈출 (소인수분해)
  '수포자를 위한 소인수분해': 'NS08',
  '소인수분해 입문':    'NS08',
}

// ──────────────────────────────────────────────────────────────
// 내부 헬퍼: 영상 배열에서 태그 기반 필터링
// ──────────────────────────────────────────────────────────────
function _filterByTag(pool: ConceptVideo[], conceptTag: string): ConceptVideo[] {
  // 1) CONCEPT_TO_STEP 직접 매핑 → step ID로 검색
  const stepId = CONCEPT_TO_STEP[conceptTag]
  if (stepId) {
    const byStep = pool.filter(v => v.conceptId === stepId)
    if (byStep.length > 0) return byStep
  }
  // 2) conceptId 직접 일치 (LS05 등 step ID로 직접 요청한 경우)
  const direct = pool.filter(v => v.conceptId === conceptTag)
  if (direct.length > 0) return direct
  // 3) 태그·레이블 부분 일치 폴백
  return pool.filter(v =>
    v.tags.some(t => t.includes(conceptTag) || conceptTag.includes(t)) ||
    v.conceptLabel.includes(conceptTag)
  )
}

// ──────────────────────────────────────────────────────────────
// Zustand 스토어
// ──────────────────────────────────────────────────────────────
interface VideoState {
  data: VideoStoreData

  // ── 조회 ──────────────────────────────────────────────────
  /** 특정 개념 ID의 승인된 영상 목록 */
  getApprovedVideos: (conceptId: string) => ConceptVideo[]
  /** 개념 태그 → approved 영상만 (버튼 카운트·플레이어 전용) */
  getApprovedByTag: (conceptTag: string) => ConceptVideo[]
  /** 개념 태그 → recruiting 슬롯만 (모집 중 안내 전용) */
  getRecruitingByTag: (conceptTag: string) => ConceptVideo[]
  /** 개념 태그(문자열) → step ID → approved+recruiting 혼합 (내부용) */
  getVideosByConceptTag: (conceptTag: string) => ConceptVideo[]
  /** 전체 영상 (관리자용) */
  getAllVideos: () => ConceptVideo[]
  /** 크리에이터 크레딧 정보 */
  getCreatorStats: (creatorId: string) => { totalCredit: number; paidCredit: number; pendingCredit: number; videoCount: number; totalViews: number }

  // ── 시청 ──────────────────────────────────────────────────
  /** 시청 시작 — viewId 반환 */
  startView: (videoId: string, viewerId: string) => string
  /** 시청 완료 기록 (watchedSec 업데이트 → 유효 여부 판정 → 크레딧 적립) */
  completeView: (viewId: string, watchedSec: number) => { isValid: boolean; creditEarned: number }

  // ── 평점 ──────────────────────────────────────────────────
  rateVideo: (videoId: string, viewerId: string, score: number) => void

  // ── 크리에이터 등록 ───────────────────────────────────────
  submitVideo: (payload: Omit<ConceptVideo, 'id' | 'status' | 'viewCount' | 'validViewCount' | 'rating' | 'ratingCount' | 'submittedAt'>) => string
  registerCreator: (profile: Omit<CreatorProfile, 'id' | 'totalCredit' | 'paidCredit' | 'joinedAt'>) => string

  // ── 관리자 ────────────────────────────────────────────────
  approveVideo: (videoId: string, adminNote?: string) => void
  rejectVideo:  (videoId: string, adminNote: string) => void

  // ── 초기화 ────────────────────────────────────────────────
  init: () => void
}

export const useVideoStore = create<VideoState>((set, get) => ({
  data: storage.load(),

  init() {
    set({ data: storage.load() })
  },

  // ── 조회 ──────────────────────────────────────────────────
  getApprovedVideos(conceptId) {
    return get().data.videos.filter(
      v => v.status === 'approved' && v.conceptId === conceptId
    )
  },

  // ── 내부 태그 검색 공통 로직 ────────────────────────────────
  getVideosByConceptTag(conceptTag) {
    const visible = get().data.videos.filter(
      v => v.status === 'approved' || v.status === 'recruiting'
    )
    return _filterByTag(visible, conceptTag)
  },

  getApprovedByTag(conceptTag) {
    const approved = get().data.videos.filter(v => v.status === 'approved')
    return _filterByTag(approved, conceptTag)
  },

  getRecruitingByTag(conceptTag) {
    const recruiting = get().data.videos.filter(v => v.status === 'recruiting')
    return _filterByTag(recruiting, conceptTag)
  },

  getAllVideos() {
    return get().data.videos
  },

  getCreatorStats(creatorId) {
    const videos = get().data.videos.filter(v => v.creatorId === creatorId)
    const credits = get().data.credits.filter(c => c.creatorId === creatorId)
    const totalCredit   = credits.reduce((s, c) => s + c.amount, 0)
    const paidCredit    = credits.filter(c => c.status === 'paid').reduce((s, c) => s + c.amount, 0)
    const pendingCredit = totalCredit - paidCredit
    const totalViews    = videos.reduce((s, v) => s + v.validViewCount, 0)
    return { totalCredit, paidCredit, pendingCredit, videoCount: videos.length, totalViews }
  },

  // ── 시청 ──────────────────────────────────────────────────
  startView(videoId, viewerId) {
    const viewId = `view_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
    const video = get().data.videos.find(v => v.id === videoId)
    if (!video) return viewId

    const record: ViewRecord = {
      id: viewId, videoId, viewerId,
      watchedSec: 0, isValid: false,
      conceptId: video.conceptId,
      viewedAt: new Date().toISOString(),
    }
    // 총 viewCount 즉시 +1
    set(state => {
      const videos = state.data.videos.map(v =>
        v.id === videoId ? { ...v, viewCount: v.viewCount + 1 } : v
      )
      const newData = { ...state.data, videos, views: [...state.data.views, record] }
      storage.save(newData)
      return { data: newData }
    })
    return viewId
  },

  completeView(viewId, watchedSec) {
    const state = get()
    const viewIdx = state.data.views.findIndex(v => v.id === viewId)
    if (viewIdx === -1) return { isValid: false, creditEarned: 0 }

    const view    = state.data.views[viewIdx]
    const video   = state.data.videos.find(v => v.id === view.videoId)
    if (!video) return { isValid: false, creditEarned: 0 }

    // 어뷰징 방지: 같은 시청자가 오늘 이미 유효 시청했는지 확인
    const today = new Date().toISOString().slice(0, 10)
    const alreadyValid = state.data.views.some(v =>
      v.id !== viewId &&
      v.videoId === view.videoId &&
      v.viewerId === view.viewerId &&
      v.isValid &&
      v.viewedAt.slice(0, 10) === today
    )

    const ratio   = watchedSec / Math.max(video.durationSec, 1)
    const isValid = !alreadyValid &&
                    watchedSec >= MIN_WATCH_SEC &&
                    ratio >= MIN_WATCH_RATIO

    const creditEarned = isValid ? CREDIT_PER_VIEW : 0

    set(s => {
      const views = [...s.data.views]
      views[viewIdx] = { ...views[viewIdx], watchedSec, isValid }

      let videos = s.data.videos
      let credits = s.data.credits

      if (isValid) {
        // validViewCount +1
        videos = videos.map(v =>
          v.id === view.videoId ? { ...v, validViewCount: v.validViewCount + 1 } : v
        )
        // 크레딧 원장 추가
        const entry: CreditEntry = {
          id:        `credit_${Date.now()}`,
          creatorId: video.creatorId,
          videoId:   view.videoId,
          viewId,
          amount:    CREDIT_PER_VIEW,
          status:    'pending',
          createdAt: new Date().toISOString(),
        }
        credits = [...credits, entry]
      }

      const newData = { ...s.data, views, videos, credits }
      storage.save(newData)
      return { data: newData }
    })

    return { isValid, creditEarned }
  },

  // ── 평점 ──────────────────────────────────────────────────
  rateVideo(videoId, viewerId, score) {
    set(state => {
      const existingIdx = state.data.ratings.findIndex(
        r => r.videoId === videoId && r.viewerId === viewerId
      )
      const newRating: VideoRating = {
        videoId, viewerId, score,
        ratedAt: new Date().toISOString(),
      }
      const ratings = existingIdx >= 0
        ? state.data.ratings.map((r, i) => i === existingIdx ? newRating : r)
        : [...state.data.ratings, newRating]

      // 해당 영상 평균 평점 업데이트
      const videoRatings = ratings.filter(r => r.videoId === videoId)
      const avg = videoRatings.reduce((s, r) => s + r.score, 0) / videoRatings.length

      const videos = state.data.videos.map(v =>
        v.id === videoId
          ? { ...v, rating: Math.round(avg * 10) / 10, ratingCount: videoRatings.length }
          : v
      )
      const newData = { ...state.data, ratings, videos }
      storage.save(newData)
      return { data: newData }
    })
  },

  // ── 크리에이터 ────────────────────────────────────────────
  submitVideo(payload) {
    const id = `video_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
    const video: ConceptVideo = {
      ...payload,
      id,
      status:         'pending',
      viewCount:      0,
      validViewCount: 0,
      rating:         0,
      ratingCount:    0,
      submittedAt:    new Date().toISOString(),
    }
    set(state => {
      const newData = { ...state.data, videos: [video, ...state.data.videos] }
      storage.save(newData)
      return { data: newData }
    })
    return id
  },

  registerCreator(profile) {
    const id = `creator_${Date.now()}`
    const creator: CreatorProfile = {
      ...profile, id,
      totalCredit: 0, paidCredit: 0,
      joinedAt: new Date().toISOString(),
    }
    set(state => {
      const newData = { ...state.data, creators: [...state.data.creators, creator] }
      storage.save(newData)
      return { data: newData }
    })
    return id
  },

  // ── 관리자 ────────────────────────────────────────────────
  approveVideo(videoId, adminNote) {
    set(state => {
      const videos = state.data.videos.map(v =>
        v.id === videoId
          ? { ...v, status: 'approved' as VideoStatus, approvedAt: new Date().toISOString(), adminNote }
          : v
      )
      const newData = { ...state.data, videos }
      storage.save(newData)
      return { data: newData }
    })
  },

  rejectVideo(videoId, adminNote) {
    set(state => {
      const videos = state.data.videos.map(v =>
        v.id === videoId
          ? { ...v, status: 'rejected' as VideoStatus, adminNote }
          : v
      )
      const newData = { ...state.data, videos }
      storage.save(newData)
      return { data: newData }
    })
  },
}))
